import { useEffect, useState } from 'react';
import { Link } from 'react-router';

import { useAuth } from '../auth';
import {
  getPortalBookingState,
  PortalApiError,
  releasePortalPhoneBooking,
} from '../services/portalApi';
import type { PortalBookingStateResponse } from '../types/api';
import { formatPhoneroUsername } from '../utils/phonero';

const phoneroPortalUrl = 'https://bn.phonero.no';

export function MyBookingPage() {
  const { getAccessToken } = useAuth();
  const [state, setState] = useState<PortalBookingStateResponse | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState(true);

  async function load(): Promise<void> {
    const token = await getAccessToken();
    setState(await getPortalBookingState(token));
  }

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const token = await getAccessToken();
        const response = await getPortalBookingState(token);
        if (!cancelled) setState(response);
      } catch (error) {
        if (!cancelled) setMessage(toMessage(error));
      } finally {
        if (!cancelled) setIsBusy(false);
      }
    })();
    return () => { cancelled = true; };
  }, [getAccessToken]);

  async function release(): Promise<void> {
    const booking = state?.Booking;
    if (!booking || !window.confirm(`Release ${booking.PhoneNumber}?`)) return;
    setIsBusy(true);
    setMessage(null);
    try {
      const token = await getAccessToken();
      await releasePortalPhoneBooking(token, booking.BookingId);
      await load();
      setMessage('The telephone has been released.');
    } catch (error) {
      setMessage(toMessage(error));
    } finally {
      setIsBusy(false);
    }
  }

  const booking = state?.Booking;
  const loginUsername = booking ? formatPhoneroUsername(booking.PhoneNumber) : null;
  const callerDisplay = booking
    ? [booking.CallerDisplayNumberName, booking.CallerDisplayNumber]
      .filter(Boolean)
      .join(' — ')
    : '';
  return (
    <section className="booking-page">
      <p className="eyebrow">Telephone booking</p>
      <h1>My booking</h1>

      {booking ? (
        <div className="booking-card booking-credentials">
          <div>
            <span>Telephone number</span>
            <strong>{loginUsername}</strong>
          </div>
          <div>
            <span>Login username</span>
            <strong>{loginUsername}</strong>
          </div>
          <div>
            <span>Password</span>
            <strong className="booking-password">{booking.Password}</strong>
          </div>
          <div>
            <span>Login email</span>
            <strong>{booking.Email}</strong>
          </div>
          <div>
            <span>Phonero portal</span>
            <strong>
              <a href={phoneroPortalUrl} target="_blank" rel="noreferrer">
                {phoneroPortalUrl} ↗
              </a>
            </strong>
          </div>
          <div>
            <span>Event</span>
            <strong>{booking.EventName ?? 'Standalone booking'}</strong>
          </div>
          {callerDisplay ? (
            <div>
              <span>Caller display number</span>
              <strong>{callerDisplay}</strong>
            </div>
          ) : null}
          <div>
            <span>Available until</span>
            <strong>{formatDateTime(booking.EndUtc)}</strong>
          </div>

          <p className="booking-security-note">
            These credentials belong to your active booking. Do not share them.
            A Phonero two-factor code will be forwarded to you when logging in.
          </p>
          <button className="secondary-button booking-release-button"
            type="button" disabled={isBusy}
            onClick={() => void release()}>
            {isBusy ? 'Releasing…' : 'Release phone'}
          </button>
        </div>
      ) : state && !isBusy ? (
        <div className="booking-card">
          <h2>No active booking</h2>
          <p>You do not currently have a telephone assigned.</p>
          <Link className="primary-button button-link" to="/book">
            Book a phone
          </Link>
        </div>
      ) : null}

      {isBusy && !state ? <p>Loading your booking…</p> : null}
      {message ? <p className="api-message" role="status">{message}</p> : null}
    </section>
  );
}

function formatDateTime(value: string): string {
  return new Intl.DateTimeFormat('en-GB', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
}

function toMessage(error: unknown): string {
  if (error instanceof PortalApiError) return error.message;
  return 'Your booking could not be loaded.';
}
